import { Injectable, TemplateRef } from '@angular/core';
import { AlertModalWindowComponent } from '../../components/alert-modal-window/alert-modal-window.component';
import { ConfirmationModalWindowComponent } from '../../components/confirmation-modal-window/confirmation-modal-window.component';
import { ConfirmationModalWindowInputKeys } from '../../components/confirmation-modal-window/constants';
import { ModalWindowService } from '../modal-window/modal-window.service';
import { ModalWindowWithSwitcher, UploadFilesModalContextData } from '../../models';
import { UploadFilesModalComponent } from '../../components/upload-files-modal/upload-files-modal.component';

@Injectable()
export class GenericModalsService {
  constructor(private modalWindow: ModalWindowService) {}

  /**
   * Opens confirmation modal and returns promise that:
   * 1. gets resolved with true when an action was confirmed
   * 2. gets resolved with false when an action was rejected
   * @param params configuration for the confirmation modal
   * @param confirmationFunc function which will be called after the confirm button click
   * @param dismissFunc function which will be called after the cancel button click
   */
  openConfirmationModal(
    params: {
      confirmTranslationKey: string;
      dismissTranslationKey?: string;
      questionTranslationKey: string;
      aftermathTranslationKey: string;
      localStorageKey?: string;
      dontShowTranslationKey?: string;
      icon?: string;
      isCloseBtnDisplay?: boolean;
      aftermathTemplate?: TemplateRef<any>;
      closeOnBackgroundClick?: boolean;
      keepModalOpenedAfterConfirming?: boolean,
      translationKeyAfterConfirming?: string,
      iconPathAfterConfirming?: string,
      displayTimeForModifiedTextAfterConfirming?: number,
      confirmButtonClasses?: string,
      questionTranslationParams?: any;
      aftermathTranslationParams?: any;
      confirmIcon?: string;
      dismissIcon?: string;
      primaryButtonFirst?: boolean;
    },
    confirmationFunc?: () => Promise<boolean> | Promise<void>,
    dismissFunc?: () => Promise<boolean> | Promise<void>,
  ): Promise<boolean> {
    return new Promise((resolve, _) => {
      this.modalWindow.openInSwitcher({
        componentsToSwitch: [
          {
            id: 'confirmation-modal',
            componentType: ConfirmationModalWindowComponent,
            contextData: {
              [ConfirmationModalWindowInputKeys.icon]: params.icon ? params.icon : 'status_not_started',
              [ConfirmationModalWindowInputKeys.closeModalAfterDismissing]: true,
              [ConfirmationModalWindowInputKeys.keepModalOpenedAfterConfirming]: params.keepModalOpenedAfterConfirming,
              [ConfirmationModalWindowInputKeys.translationKeyAfterConfirming]: params.translationKeyAfterConfirming,
              [ConfirmationModalWindowInputKeys.iconPathAfterConfirming]: params.iconPathAfterConfirming,
              [ConfirmationModalWindowInputKeys.displayTimeForModifiedTextAfterConfirming]: params.displayTimeForModifiedTextAfterConfirming,
              [ConfirmationModalWindowInputKeys.confirmButtonClasses]: params.confirmButtonClasses,
              [ConfirmationModalWindowInputKeys.confirmTranslationKey]: params.confirmTranslationKey,
              [ConfirmationModalWindowInputKeys.dismissTranslationKey]: params.dismissTranslationKey,
              [ConfirmationModalWindowInputKeys.questionTranslationKey]: params.questionTranslationKey,
              [ConfirmationModalWindowInputKeys.questionTranslationParams]: params.questionTranslationParams,
              [ConfirmationModalWindowInputKeys.aftermathTranslationKey]: params.aftermathTranslationKey,
              [ConfirmationModalWindowInputKeys.aftermathTemplate]: params.aftermathTemplate,
              [ConfirmationModalWindowInputKeys.confirmationHandlerFunction]: confirmationFunc
                ? confirmationFunc
                : () => resolve(true),
              [ConfirmationModalWindowInputKeys.dismissHandlerFunction]: dismissFunc
                ? dismissFunc
                : () => resolve(false),
              [ConfirmationModalWindowInputKeys.localStorageKey]: params.localStorageKey,
              [ConfirmationModalWindowInputKeys.dontShowTranslationKey]: params.dontShowTranslationKey,
              [ConfirmationModalWindowInputKeys.aftermathTranslationParams]: params.aftermathTranslationParams,
              [ConfirmationModalWindowInputKeys.confirmIcon]: params.confirmIcon,
              [ConfirmationModalWindowInputKeys.dismissIcon]: params.dismissIcon,
              [ConfirmationModalWindowInputKeys.primaryButtonFirst]: params.primaryButtonFirst !== false,
            },
          },
        ],
        options: {
          closeOnBackgroundClick: params.closeOnBackgroundClick,
          closeBtnDisplay: params.isCloseBtnDisplay,
        }
      });
    });
  }

  openSuccessAlert(params: { text: string }): void {
    this.modalWindow.openInSwitcher({
      id: 'allert',
      componentsToSwitch: [
        {
          id: 'allert',
          componentType: AlertModalWindowComponent,
        },
      ],
      context: {
        text: params.text,
        type: 'success',
      },
    });
  }

  openUploadFilesModal(contextData: UploadFilesModalContextData): void {
    const modalWindowSwitcher: ModalWindowWithSwitcher<UploadFilesModalContextData> = {
      componentsToSwitch: [
        {
          id: 'upload-files-modal',
          componentType: UploadFilesModalComponent,
          contextData,
        },
      ],
      options: {
        closeOnBackgroundClick: false,
      },
    };

    this.modalWindow.openInSwitcher(modalWindowSwitcher);
  }
}
